import React, { useState, useEffect } from 'react';
import { apiService } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { X, ArrowRight, AlertCircle, Percent } from 'lucide-react';

type InitNextInvoiceModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  quoteId: number;
  currentInvoiceNumber: string;
};

export const InitNextInvoiceModal: React.FC<InitNextInvoiceModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  quoteId,
  currentInvoiceNumber
}) => {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [remainingPercentage, setRemainingPercentage] = useState(0);
  const [alreadyBilled, setAlreadyBilled] = useState(0);
  
  const [formData, setFormData] = useState({
    percentage: 0,
    payment_label: '',
    due_days: 30,
    notes: '',
    payment_terms: 'Paiement à 30 jours'
  });

  useEffect(() => {
    if (isOpen && quoteId) {
      void loadRemainingPercentage();
    }
  }, [isOpen, quoteId]);

  const loadRemainingPercentage = async () => {
    try {
      setLoading(true);
      const response = await apiService.get(`/invoices/quotes/${quoteId}/remaining-percentage`);
      const remaining = response.remaining_percentage || 0;
      const billed = response.already_billed_percentage || 0;
      
      setRemainingPercentage(remaining);
      setAlreadyBilled(billed);
      setFormData(prev => ({ ...prev, percentage: remaining }));
    } catch (e: any) {
      toast({
        title: 'Erreur',
        description: 'Impossible de récupérer les informations du devis.',
        variant: 'destructive'
      });
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.percentage <= 0 || formData.percentage > remainingPercentage) {
      toast({
        title: 'Pourcentage invalide',
        description: `Le pourcentage doit être entre 1 et ${remainingPercentage}%.`,
        variant: 'destructive'
      });
      return;
    }

    try {
      setBusy(true);
      await apiService.post(`/invoices/quotes/${quoteId}/init-next`, {
        percentage: formData.percentage,
        payment_label: formData.payment_label || null,
        due_days: formData.due_days,
        notes: formData.notes || null,
        payment_terms: formData.payment_terms || null
      });

      toast({
        title: 'Facture créée',
        description: `Nouvelle facture de ${formData.percentage}% créée avec succès.`
      });
      
      onSuccess();
      onClose();
      resetForm();
    } catch (e: any) {
      toast({
        title: 'Erreur',
        description: e?.response?.data?.detail || 'Création impossible.',
        variant: 'destructive'
      });
    } finally {
      setBusy(false);
    }
  };

  const resetForm = () => {
    setFormData({
      percentage: 0,
      payment_label: '',
      due_days: 30,
      notes: '',
      payment_terms: 'Paiement à 30 jours'
    });
  };

  const handleClose = () => {
    if (!busy) {
      resetForm();
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-slate-200 dark:border-slate-700">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg">
              <ArrowRight className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">
                Créer la facture suivante
              </h2>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Depuis: {currentInvoiceNumber}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={busy}
            className="p-2 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
            </div>
          ) : remainingPercentage <= 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="h-16 w-16 text-orange-500 mb-4" />
              <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100 mb-2">
                Devis entièrement facturé
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Ce devis a déjà été facturé à 100%. Aucune facture supplémentaire ne peut être créée.
              </p>
              <Button
                onClick={handleClose}
                className="mt-6 bg-slate-600 hover:bg-slate-700"
              >
                Fermer
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Info Box */}
              <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
                      Récapitulatif du devis
                    </h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-blue-700 dark:text-blue-300">Déjà facturé:</span>
                        <span className="ml-2 font-bold text-blue-900 dark:text-blue-100">
                          {alreadyBilled}%
                        </span>
                      </div>
                      <div>
                        <span className="text-blue-700 dark:text-blue-300">Reste à facturer:</span>
                        <span className="ml-2 font-bold text-green-600 dark:text-green-400">
                          {remainingPercentage}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Percentage */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                  Pourcentage de cette tranche <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="1"
                    max={remainingPercentage}
                    value={formData.percentage}
                    onChange={(e) => setFormData({ ...formData, percentage: parseInt(e.target.value) || 0 })}
                    required
                    className="w-full h-12 pl-4 pr-12 rounded-xl border-2 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder={`Max: ${remainingPercentage}%`}
                  />
                  <Percent className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Maximum: {remainingPercentage}% • Laissez à {remainingPercentage}% pour facturer le solde complet
                </p>
              </div>

              {/* Payment Label */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                  Libellé de la tranche
                </label>
                <input
                  type="text"
                  value={formData.payment_label}
                  onChange={(e) => setFormData({ ...formData, payment_label: e.target.value })}
                  maxLength={100}
                  className="w-full h-12 px-4 rounded-xl border-2 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Ex: Acompte 2, Solde, Paiement final..."
                />
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Optionnel • Apparaîtra sur la facture et dans la liste
                </p>
              </div>

              {/* Due Days */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                  Échéance (jours)
                </label>
                <select
                  value={formData.due_days}
                  onChange={(e) => setFormData({ ...formData, due_days: parseInt(e.target.value) })}
                  className="w-full h-12 px-4 rounded-xl border-2 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value={7}>7 jours</option>
                  <option value={15}>15 jours</option>
                  <option value={30}>30 jours</option>
                  <option value={45}>45 jours</option>
                  <option value={60}>60 jours</option>
                  <option value={90}>90 jours</option>
                </select>
              </div>

              {/* Payment Terms */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                  Conditions de paiement
                </label>
                <input
                  type="text"
                  value={formData.payment_terms}
                  onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
                  maxLength={500}
                  className="w-full h-12 px-4 rounded-xl border-2 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Ex: Paiement à 30 jours, Virement bancaire..."
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                  Notes internes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  maxLength={2000}
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl border-2 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                  placeholder="Notes additionnelles pour cette facture..."
                />
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        {!loading && remainingPercentage > 0 && (
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={busy}
              className="hover:bg-slate-200 dark:hover:bg-slate-700"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              onClick={handleSubmit}
              disabled={busy || formData.percentage <= 0 || formData.percentage > remainingPercentage}
              className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Création...
                </>
              ) : (
                <>
                  <ArrowRight className="h-4 w-4 mr-2" />
                  Créer la facture ({formData.percentage}%)
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
